import { DmnModel, ColumnDefinition, Rule, HitPolicy, ColumnType, DataType } from '../types';
import { validateModel, validateCell } from './validation';

/**
 * EditorAiCapability — the structural contract the admin AI assistant consumes.
 *
 * Defined LOCALLY (no dependency on the admin app) so this editor stays standalone.
 * The shapes mirror the admin's `types/ai.ts` (EditorAiCapability / EditorToolResult):
 * while the editor is focused, its `tools` join the assistant's tool set, and calling
 * one runs `invoke` INSIDE the editor (client-side), which validates and may reject.
 */
export interface EditorToolResult {
  ok: boolean;
  result?: unknown;
  rejected?: boolean;
  reason?: string;
}

export interface EditorToolSpec {
  name: string;
  description: string;
  /** JSON Schema object describing the tool's arguments. */
  parameters: Record<string, unknown>;
}

export interface EditorAiCapability {
  kind: string;
  guidelines: string;
  tools: EditorToolSpec[];
  invoke: (toolName: string, args: Record<string, unknown>) => Promise<EditorToolResult>;
}

/**
 * A persistent selection/highlight over the table. Any combination may be set; the editor
 * paints the matching headers/rows/cells. Cells are addressed as `${ruleId}:${columnId}`.
 */
export interface DmnSelection {
  columnIds: string[];
  ruleIds: string[];
  /** Cell keys, each `${ruleId}:${columnId}`. */
  cells: string[];
}

/** The live model + viewport/selection accessors the capability needs from the editor. */
export interface DmnModelAccess {
  /** Always returns the LATEST model (read through a ref, not a stale closure). */
  getModel: () => DmnModel | null;
  /** Applies a new model live (re-renders the editor). Same path the UI uses. */
  setModel: (model: DmnModel) => void;
  /**
   * Optional: the DOM root the table is rendered into. Used by navigation tools to find the
   * element to scroll into view. Defaults to the global `document` when omitted (or unavailable,
   * e.g. unit tests / SSR), in which case navigation tools no-op gracefully instead of throwing.
   */
  getContainer?: () => ParentNode | null;
  /**
   * Optional: applies a persistent selection/highlight (the same state the UI paints). When
   * omitted, the selection tools report they are unsupported instead of throwing.
   */
  setSelection?: (selection: DmnSelection) => void;
}

const COLUMN_TYPES: ColumnType[] = ['input', 'output', 'annotation'];
const DATA_TYPES: DataType[] = ['string', 'number', 'boolean', 'date'];
const HIT_POLICIES: HitPolicy[] = ['UNIQUE', 'FIRST', 'PRIORITY', 'ANY', 'COLLECT', 'RULE ORDER', 'OUTPUT ORDER'];

const ok = (result?: unknown): EditorToolResult => ({ ok: true, result });
const reject = (reason: string): EditorToolResult => ({ ok: false, rejected: true, reason });

/**
 * Stable DOM hooks the table renders so navigation tools can find elements WITHOUT coupling to
 * Tailwind classes. Mirrored by components/molecules/TableParts.tsx + DecisionTable.tsx.
 */
export const DMN_DATA_ATTR = {
  column: 'data-dmn-col', // on the column <th>
  rule: 'data-dmn-rule', // on the rule <tr>
  cell: 'data-dmn-cell', // on the cell <td>, value = `${ruleId}:${columnId}`
} as const;

const escapeAttr = (value: string) => {
  // Prefer the native escaper; fall back to a conservative manual escape (older test envs).
  const cssAny = typeof globalThis !== 'undefined' ? (globalThis as any).CSS : undefined;
  if (cssAny && typeof cssAny.escape === 'function') return cssAny.escape(value);
  return value.replace(/["\\]/g, '\\$&');
};

/**
 * Scroll `el` into view and flash a transient highlight. Pure DOM, guarded for non-browser envs.
 * The flash uses an inline outline (no dependency on the stylesheet) and self-clears.
 */
const flashIntoView = (el: Element | null): boolean => {
  if (!el || typeof (el as HTMLElement).scrollIntoView !== 'function') return false;
  try {
    (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  } catch {
    (el as HTMLElement).scrollIntoView();
  }
  const style = (el as HTMLElement).style;
  if (style) {
    const prevOutline = style.outline;
    const prevOffset = style.outlineOffset;
    style.outline = '2px solid #10b981';
    style.outlineOffset = '-2px';
    const w = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
    if (w && typeof w.setTimeout === 'function') {
      w.setTimeout(() => {
        style.outline = prevOutline;
        style.outlineOffset = prevOffset;
      }, 1400);
    }
  }
  return true;
};

/**
 * Resolve the DOM root to query. Prefers the host-provided container; falls back to the global
 * `document`. Returns null in non-browser environments so navigation tools no-op (never throw).
 */
const resolveContainer = (access: DmnModelAccess): ParentNode | null => {
  if (access.getContainer) {
    const c = access.getContainer();
    if (c) return c;
  }
  const doc = typeof globalThis !== 'undefined' ? (globalThis as any).document : undefined;
  return (doc as ParentNode | undefined) ?? null;
};

/** Coerce an optional arg into a string[]; returns null if it is present but not an array of strings. */
const toStringArray = (value: unknown): string[] | null => {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return null;
  if (!value.every((v) => typeof v === 'string')) return null;
  return value as string[];
};

/** Compact JSON view of the model — small, model-aware, no UI noise. */
const compactModel = (m: DmnModel) => ({
  id: m.id,
  name: m.name,
  hitPolicy: m.hitPolicy,
  columns: m.columns.map((c) => ({
    id: c.id,
    label: c.label,
    type: c.type,
    dataType: c.dataType,
    variableName: c.variableName,
  })),
  rules: m.rules.map((r) => ({
    id: r.id,
    cells: m.columns.reduce<Record<string, string>>((acc, c) => {
      acc[c.id] = r.cells[c.id] ?? '';
      return acc;
    }, {}),
  })),
});

let idCounter = 0;
const freshId = (prefix: string) => `${prefix}${Date.now()}_${idCounter++}`;

/**
 * Run the editor's REAL validator on a candidate model and describe its invalid cells.
 * Maps each invalidCells key (`${ruleId}-${colId}`) back to a human-readable cell location.
 */
const describeInvalidCells = (model: DmnModel, invalidCells: Record<string, boolean>): string[] => {
  const colById = new Map(model.columns.map((c) => [c.id, c]));
  return Object.keys(invalidCells).map((key) => {
    const dash = key.lastIndexOf('-');
    const ruleId = key.slice(0, dash);
    const colId = key.slice(dash + 1);
    const col = colById.get(colId);
    const value = model.rules.find((r) => r.id === ruleId)?.cells[colId] ?? '';
    const where = col ? `column "${col.label}" (${col.dataType})` : `column "${colId}"`;
    return `rule "${ruleId}" / ${where}: value "${value}" is invalid`;
  });
};

/**
 * Apply `next` ONLY if it does not introduce validation errors that the current model did not
 * already have; otherwise reject with the validator's reasons. This is the single gate every
 * mutating tool routes through: candidate model in -> real validateModel -> apply or reject.
 */
const applyIfValid = (
  prev: DmnModel,
  next: DmnModel,
  setModel: (m: DmnModel) => void,
  result?: unknown,
): EditorToolResult => {
  const before = validateModel(prev);
  const after = validateModel(next);
  // Reject only when the change introduces NEW invalid cells (don't punish pre-existing errors).
  const newlyInvalid = Object.keys(after.invalidCells).filter((k) => !before.invalidCells[k]);
  if (newlyInvalid.length > 0) {
    const reasons = describeInvalidCells(next, Object.fromEntries(newlyInvalid.map((k) => [k, true])));
    return reject(`Change rejected: it introduces ${newlyInvalid.length} validation error(s): ${reasons.join('; ')}.`);
  }
  setModel(next);
  return ok(result);
};

/**
 * Build the DMN editor's AI capability over a live model accessor. Pure function (no React)
 * so it is unit-testable; the React hook just wires getModel/setModel to component state.
 *
 * Every mutating tool routes through the SAME semantics the DecisionTable UI uses and then
 * re-validates, so invalid input is rejected with a reason INSTEAD of corrupting the model.
 */
export function createDmnAiCapability(access: DmnModelAccess): EditorAiCapability {
  const tools: EditorToolSpec[] = [
    {
      name: 'get_model',
      description:
        'Return the current DMN decision table as compact JSON: id, name, hitPolicy, columns (input/output/annotation, each with dataType + variableName) and rules (cells keyed by column id).',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'list_element_types',
      description:
        'List the valid column types, data types and hit policies this DMN editor supports.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'add_column',
      description:
        'Add an input, output or annotation column. Inputs are inserted before the first output (matching the editor). Returns the new column id.',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: COLUMN_TYPES, description: 'Column type.' },
          label: { type: 'string', description: 'Human-readable header label.' },
          dataType: { type: 'string', enum: DATA_TYPES, description: 'FEEL data type. Defaults to string.' },
          variableName: { type: 'string', description: 'FEEL variable / output name.' },
        },
        required: ['type', 'label'],
        additionalProperties: false,
      },
    },
    {
      name: 'update_column',
      description: 'Update an existing column (label, dataType, variableName, type) by id.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Column id to update.' },
          label: { type: 'string' },
          dataType: { type: 'string', enum: DATA_TYPES },
          variableName: { type: 'string' },
          type: { type: 'string', enum: COLUMN_TYPES },
        },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'delete_column',
      description: 'Delete a column by id and drop its cells from every rule.',
      parameters: {
        type: 'object',
        properties: { id: { type: 'string', description: 'Column id to delete.' } },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'add_rule',
      description:
        'Add a rule. Optionally provide cells (map of column id -> FEEL value). Unknown column ids and cells that fail the column dataType validation are rejected.',
      parameters: {
        type: 'object',
        properties: {
          cells: {
            type: 'object',
            description: 'Map of column id -> cell value (FEEL text). Omitted columns default to empty.',
            additionalProperties: { type: 'string' },
          },
        },
        additionalProperties: false,
      },
    },
    {
      name: 'update_rule',
      description:
        'Set cell values on an existing rule (map of column id -> value). Unknown column ids and values failing dataType validation are rejected.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Rule id to update.' },
          cells: { type: 'object', additionalProperties: { type: 'string' } },
        },
        required: ['id', 'cells'],
        additionalProperties: false,
      },
    },
    {
      name: 'delete_rule',
      description: 'Delete a rule by id.',
      parameters: {
        type: 'object',
        properties: { id: { type: 'string', description: 'Rule id to delete.' } },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'set_hit_policy',
      description: 'Set the decision table hit policy.',
      parameters: {
        type: 'object',
        properties: { hitPolicy: { type: 'string', enum: HIT_POLICIES } },
        required: ['hitPolicy'],
        additionalProperties: false,
      },
    },
    {
      name: 'validate',
      description:
        'Validate the whole model against the editor rules. Returns isValid, errorCount and the list of invalid {ruleId, columnId} cells.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'replace_model',
      description:
        'Replace the ENTIRE model from JSON ({id,name,hitPolicy,columns,rules}). Validated structurally before applying; rejected if malformed.',
      parameters: {
        type: 'object',
        properties: {
          model: { type: 'object', description: 'A full DmnModel JSON object.' },
        },
        required: ['model'],
        additionalProperties: false,
      },
    },

    // --- Navigation / viewport (non-mutating: never change the model) ---
    {
      name: 'focus_cell',
      description:
        'Scroll a single cell into view and briefly highlight it so the user sees the cell you mean. ' +
        'Does NOT change the model. Rejects unknown rule or column ids.',
      parameters: {
        type: 'object',
        properties: {
          ruleId: { type: 'string', description: 'Rule (row) id of the cell.' },
          columnId: { type: 'string', description: 'Column id of the cell.' },
        },
        required: ['ruleId', 'columnId'],
        additionalProperties: false,
      },
    },
    {
      name: 'focus_column',
      description:
        'Scroll a column header into view and briefly highlight the whole column. Does NOT change the model. Rejects an unknown column id.',
      parameters: {
        type: 'object',
        properties: {
          columnId: { type: 'string', description: 'Column id to reveal.' },
        },
        required: ['columnId'],
        additionalProperties: false,
      },
    },
    {
      name: 'focus_rule',
      description:
        'Scroll a rule (row) into view and briefly highlight it. Does NOT change the model. Rejects an unknown rule id.',
      parameters: {
        type: 'object',
        properties: {
          ruleId: { type: 'string', description: 'Rule id to reveal.' },
        },
        required: ['ruleId'],
        additionalProperties: false,
      },
    },

    // --- Selection / highlight (non-mutating persistent highlight) ---
    {
      name: 'select',
      description:
        'Persistently highlight a set of columns, rules and/or cells so the user can see what you are referring to. ' +
        'Replaces any previous selection. Provide at least one of columnIds, ruleIds, cells. ' +
        'Does NOT change the model. Rejects any unknown column/rule id or malformed cell key.',
      parameters: {
        type: 'object',
        properties: {
          columnIds: { type: 'array', items: { type: 'string' }, description: 'Column ids to highlight.' },
          ruleIds: { type: 'array', items: { type: 'string' }, description: 'Rule ids to highlight.' },
          cells: {
            type: 'array',
            items: { type: 'string' },
            description: 'Cell keys to highlight, each formatted "ruleId:columnId".',
          },
        },
        additionalProperties: false,
      },
    },
    {
      name: 'clear_selection',
      description: 'Clear any highlighted columns, rules and cells. Does NOT change the model.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
  ];

  const guidelines =
    'This editor edits a single DMN decision table. The model is { id, name, hitPolicy, columns[], rules[] }. ' +
    `Columns have a type (${COLUMN_TYPES.join('/')}) and a dataType (${DATA_TYPES.join('/')}); inputs come before outputs. ` +
    'Rules hold cells keyed by column id (FEEL text; "" or "-" means wildcard). ' +
    `Valid hit policies: ${HIT_POLICIES.join(', ')}. ` +
    'Use get_model / list_element_types first. Mutations (add/update/delete column or rule, set_hit_policy, replace_model) ' +
    'are validated by the editor and rejected with a reason on unknown ids, bad types, or cell values that violate the column dataType. ' +
    'Use validate to surface current errors. ' +
    'To show the user where something is WITHOUT changing the model, use the navigation tools ' +
    '(focus_cell / focus_column / focus_rule scroll-into-view + briefly flash the target) and the ' +
    'selection tools (select highlights a set of columns/rules/cells; clear_selection removes it). ' +
    'Navigation/selection never mutate the model and reject unknown ids with a reason.';

  const invoke = async (
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<EditorToolResult> => {
    const model = access.getModel();
    if (!model) return reject('The DMN model is not loaded yet.');
    const a = args || {};

    switch (toolName) {
      case 'get_model':
        return ok(compactModel(model));

      case 'list_element_types':
        return ok({ columnTypes: COLUMN_TYPES, dataTypes: DATA_TYPES, hitPolicies: HIT_POLICIES });

      case 'add_column': {
        const type = a.type as ColumnType;
        const label = a.label as string;
        if (!COLUMN_TYPES.includes(type)) return reject(`Unknown column type "${String(a.type)}". Valid: ${COLUMN_TYPES.join(', ')}.`);
        if (typeof label !== 'string' || label.trim() === '') return reject('label is required and must be a non-empty string.');
        const dataType = (a.dataType as DataType) ?? 'string';
        if (!DATA_TYPES.includes(dataType)) return reject(`Unknown dataType "${String(a.dataType)}". Valid: ${DATA_TYPES.join(', ')}.`);
        const variableName = a.variableName !== undefined ? String(a.variableName) : (type === 'output' ? 'output' : 'input');

        const newCol: ColumnDefinition = { id: freshId('c'), label, type, dataType, variableName };
        const columns = [...model.columns];
        if (type === 'input') {
          const firstOutputIndex = columns.findIndex((c) => c.type === 'output');
          if (firstOutputIndex === -1) columns.push(newCol);
          else columns.splice(firstOutputIndex, 0, newCol);
        } else {
          columns.push(newCol);
        }
        // New cells default to '' on every rule (empty = wildcard, always valid).
        const rules = model.rules.map((r) => ({ ...r, cells: { ...r.cells, [newCol.id]: '' } }));
        return applyIfValid(model, { ...model, columns, rules }, access.setModel, { id: newCol.id });
      }

      case 'update_column': {
        const id = a.id as string;
        const target = model.columns.find((c) => c.id === id);
        if (!target) return reject(`No column with id "${String(id)}".`);
        const patch: Partial<ColumnDefinition> = {};
        if (a.label !== undefined) {
          if (typeof a.label !== 'string' || a.label.trim() === '') return reject('label must be a non-empty string.');
          patch.label = a.label;
        }
        if (a.dataType !== undefined) {
          if (!DATA_TYPES.includes(a.dataType as DataType)) return reject(`Unknown dataType "${String(a.dataType)}".`);
          patch.dataType = a.dataType as DataType;
        }
        if (a.type !== undefined) {
          if (!COLUMN_TYPES.includes(a.type as ColumnType)) return reject(`Unknown column type "${String(a.type)}".`);
          patch.type = a.type as ColumnType;
        }
        if (a.variableName !== undefined) patch.variableName = String(a.variableName);

        const columns = model.columns.map((c) => (c.id === id ? { ...c, ...patch } : c));
        const next = { ...model, columns };
        // Changing dataType/type can invalidate existing cells — run the REAL validator and
        // REJECT (no setModel) if the change introduces new errors.
        return applyIfValid(model, next, access.setModel, { id });
      }

      case 'delete_column': {
        const id = a.id as string;
        if (!model.columns.some((c) => c.id === id)) return reject(`No column with id "${String(id)}".`);
        const columns = model.columns.filter((c) => c.id !== id);
        const rules = model.rules.map((r) => {
          const cells = { ...r.cells };
          delete cells[id];
          return { ...r, cells };
        });
        return applyIfValid(model, { ...model, columns, rules }, access.setModel, { deleted: id });
      }

      case 'add_rule': {
        const cellsIn = (a.cells as Record<string, unknown>) ?? {};
        const cells: Record<string, string> = {};
        model.columns.forEach((c) => (cells[c.id] = ''));
        for (const [colId, raw] of Object.entries(cellsIn)) {
          const col = model.columns.find((c) => c.id === colId);
          if (!col) return reject(`Rule references unknown column id "${colId}".`);
          const value = raw == null ? '' : String(raw);
          if (!validateCell(value, col.dataType, col.type)) {
            return reject(`Value "${value}" is invalid for column "${col.label}" (${col.dataType}).`);
          }
          cells[colId] = value;
        }
        const newRule: Rule = { id: freshId('r'), cells };
        return applyIfValid(model, { ...model, rules: [...model.rules, newRule] }, access.setModel, { id: newRule.id });
      }

      case 'update_rule': {
        const id = a.id as string;
        const target = model.rules.find((r) => r.id === id);
        if (!target) return reject(`No rule with id "${String(id)}".`);
        const cellsIn = a.cells as Record<string, unknown>;
        if (!cellsIn || typeof cellsIn !== 'object') return reject('cells must be an object of columnId -> value.');
        const nextCells = { ...target.cells };
        for (const [colId, raw] of Object.entries(cellsIn)) {
          const col = model.columns.find((c) => c.id === colId);
          if (!col) return reject(`Update references unknown column id "${colId}".`);
          const value = raw == null ? '' : String(raw);
          if (!validateCell(value, col.dataType, col.type)) {
            return reject(`Value "${value}" is invalid for column "${col.label}" (${col.dataType}).`);
          }
          nextCells[colId] = value;
        }
        const rules = model.rules.map((r) => (r.id === id ? { ...r, cells: nextCells } : r));
        return applyIfValid(model, { ...model, rules }, access.setModel, { id });
      }

      case 'delete_rule': {
        const id = a.id as string;
        if (!model.rules.some((r) => r.id === id)) return reject(`No rule with id "${String(id)}".`);
        return applyIfValid(model, { ...model, rules: model.rules.filter((r) => r.id !== id) }, access.setModel, { deleted: id });
      }

      case 'set_hit_policy': {
        const hp = a.hitPolicy as HitPolicy;
        if (!HIT_POLICIES.includes(hp)) return reject(`Unknown hit policy "${String(a.hitPolicy)}". Valid: ${HIT_POLICIES.join(', ')}.`);
        return applyIfValid(model, { ...model, hitPolicy: hp }, access.setModel, { hitPolicy: hp });
      }

      case 'validate': {
        const v = validateModel(model);
        const invalid = Object.keys(v.invalidCells).map((key) => {
          // key format: `${ruleId}-${colId}` — split on the last '-' that begins the column id.
          const dash = key.lastIndexOf('-');
          return { ruleId: key.slice(0, dash), columnId: key.slice(dash + 1) };
        });
        return ok({ isValid: v.isValid, errorCount: v.errorCount, invalidCells: invalid });
      }

      case 'replace_model': {
        const candidate = a.model as unknown;
        // 1) Structural guard: ids/types/column-refs AND every cell value must be a string
        //    (a non-string cell would crash validateCell(...).trim()).
        const err = validateModelShape(candidate);
        if (err) return reject(err);
        // 2) Run the REAL validator on the candidate; reject if any cell value is invalid.
        const nextModel = candidate as DmnModel;
        const v = validateModel(nextModel);
        if (!v.isValid) {
          const reasons = describeInvalidCells(nextModel, v.invalidCells);
          return reject(`Replacement rejected: model has ${v.errorCount} invalid cell(s): ${reasons.join('; ')}.`);
        }
        access.setModel(nextModel);
        return ok(compactModel(nextModel));
      }

      case 'focus_cell': {
        const ruleId = String(a.ruleId ?? '');
        const columnId = String(a.columnId ?? '');
        if (!model.rules.some((r) => r.id === ruleId)) return reject(`No rule with id "${ruleId}".`);
        if (!model.columns.some((c) => c.id === columnId)) return reject(`No column with id "${columnId}".`);
        const root = resolveContainer(access);
        if (!root) return ok({ focused: false, reason: 'No DOM container available (headless).' });
        const el = root.querySelector(`[${DMN_DATA_ATTR.cell}="${escapeAttr(`${ruleId}:${columnId}`)}"]`);
        if (!el) return reject(`Cell "${ruleId}:${columnId}" is not currently rendered.`);
        return ok({ focused: flashIntoView(el), cell: `${ruleId}:${columnId}` });
      }

      case 'focus_column': {
        const columnId = String(a.columnId ?? '');
        if (!model.columns.some((c) => c.id === columnId)) return reject(`No column with id "${columnId}".`);
        const root = resolveContainer(access);
        if (!root) return ok({ focused: false, reason: 'No DOM container available (headless).' });
        const el = root.querySelector(`[${DMN_DATA_ATTR.column}="${escapeAttr(columnId)}"]`);
        if (!el) return reject(`Column "${columnId}" is not currently rendered.`);
        return ok({ focused: flashIntoView(el), columnId });
      }

      case 'focus_rule': {
        const ruleId = String(a.ruleId ?? '');
        if (!model.rules.some((r) => r.id === ruleId)) return reject(`No rule with id "${ruleId}".`);
        const root = resolveContainer(access);
        if (!root) return ok({ focused: false, reason: 'No DOM container available (headless).' });
        const el = root.querySelector(`[${DMN_DATA_ATTR.rule}="${escapeAttr(ruleId)}"]`);
        if (!el) return reject(`Rule "${ruleId}" is not currently rendered.`);
        return ok({ focused: flashIntoView(el), ruleId });
      }

      case 'select': {
        if (!access.setSelection) return reject('Selection is not supported by this editor host.');
        const columnIds = toStringArray(a.columnIds);
        const ruleIds = toStringArray(a.ruleIds);
        const cells = toStringArray(a.cells);
        if (columnIds === null || ruleIds === null || cells === null) {
          return reject('columnIds, ruleIds and cells must each be arrays of strings.');
        }
        if (columnIds.length === 0 && ruleIds.length === 0 && cells.length === 0) {
          return reject('Provide at least one of columnIds, ruleIds or cells to select.');
        }
        const colSet = new Set(model.columns.map((c) => c.id));
        const ruleSet = new Set(model.rules.map((r) => r.id));
        for (const id of columnIds) if (!colSet.has(id)) return reject(`No column with id "${id}".`);
        for (const id of ruleIds) if (!ruleSet.has(id)) return reject(`No rule with id "${id}".`);
        for (const key of cells) {
          const sep = key.indexOf(':');
          if (sep === -1) return reject(`Cell key "${key}" must be formatted "ruleId:columnId".`);
          const rId = key.slice(0, sep);
          const cId = key.slice(sep + 1);
          if (!ruleSet.has(rId)) return reject(`Cell "${key}" references unknown rule id "${rId}".`);
          if (!colSet.has(cId)) return reject(`Cell "${key}" references unknown column id "${cId}".`);
        }
        access.setSelection({ columnIds, ruleIds, cells });
        return ok({ columnIds, ruleIds, cells });
      }

      case 'clear_selection': {
        if (!access.setSelection) return reject('Selection is not supported by this editor host.');
        access.setSelection({ columnIds: [], ruleIds: [], cells: [] });
        return ok({ cleared: true });
      }

      default:
        return reject(`Unknown tool "${toolName}".`);
    }
  };

  return { kind: 'dmn', guidelines, tools, invoke };
}

/** Structural guard for replace_model — rejects malformed JSON before it can corrupt the editor. */
function validateModelShape(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return 'model must be a JSON object.';
  const m = value as Record<string, unknown>;
  if (typeof m.id !== 'string') return 'model.id must be a string.';
  if (typeof m.name !== 'string') return 'model.name must be a string.';
  if (!HIT_POLICIES.includes(m.hitPolicy as HitPolicy)) return `model.hitPolicy must be one of: ${HIT_POLICIES.join(', ')}.`;
  if (!Array.isArray(m.columns)) return 'model.columns must be an array.';
  if (!Array.isArray(m.rules)) return 'model.rules must be an array.';

  const colIds = new Set<string>();
  for (const c of m.columns as unknown[]) {
    if (typeof c !== 'object' || c === null) return 'each column must be an object.';
    const col = c as Record<string, unknown>;
    if (typeof col.id !== 'string') return 'each column needs a string id.';
    if (colIds.has(col.id)) return `duplicate column id "${col.id}".`;
    colIds.add(col.id);
    if (typeof col.label !== 'string') return `column "${col.id}" needs a string label.`;
    if (!COLUMN_TYPES.includes(col.type as ColumnType)) return `column "${col.id}" has invalid type.`;
    if (!DATA_TYPES.includes(col.dataType as DataType)) return `column "${col.id}" has invalid dataType.`;
  }
  for (const r of m.rules as unknown[]) {
    if (typeof r !== 'object' || r === null) return 'each rule must be an object.';
    const rule = r as Record<string, unknown>;
    if (typeof rule.id !== 'string') return 'each rule needs a string id.';
    if (typeof rule.cells !== 'object' || rule.cells === null) return `rule "${rule.id}" needs a cells object.`;
    for (const [key, cellValue] of Object.entries(rule.cells as Record<string, unknown>)) {
      if (!colIds.has(key)) return `rule "${rule.id}" references unknown column id "${key}".`;
      // Cell values MUST be strings — validateCell calls .trim(); a non-string would throw.
      if (typeof cellValue !== 'string') return `rule "${rule.id}" cell for column "${key}" must be a string.`;
    }
  }
  return null;
}
